import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import { siteSettings, externalLinks } from '@/mocks/siteSettings';
import { books } from '@/mocks/books';
import { latestBooks2026 } from '@/mocks/latestBooks2026';
import BookSlider from '@/components/feature/BookSlider';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import GreenValeHero from '@/components/special-effects/green-vale';

/* ──────────────────────────────────────────────
   RevealWrap — scroll fade-in (one-shot)
────────────────────────────────────────────── */
function RevealWrap({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useScrollReveal(0.06);
  return (
    <div ref={ref} className={`section-reveal ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

/* ──────────────────────────────────────────────
   OrnamentDivider — classical fleuron divider
────────────────────────────────────────────── */
function OrnamentDivider() {
  return (
    <div className="ornament-divider my-12 md:my-16" aria-hidden="true">
      <div className="ornament-divider-icon font-display italic">&#10087;</div>
    </div>
  );
}

/* ──────────────────────────────────────────────
   SectionLabel — editorial numbering
────────────────────────────────────────────── */
function SectionLabel({ num, label }: { num: string; label: string }) {
  return (
    <div className="flex items-center gap-3" style={{ color: 'oklch(var(--foreground-400)/0.38)' }}>
      <span className="font-display italic font-bold text-[13px] tracking-[0.05em]">{num}</span>
      <span className="w-6 h-px" style={{ backgroundColor: 'oklch(var(--foreground-300)/0.20)' }} />
      <span className="font-label text-[11px] tracking-[0.15em] uppercase">{label}</span>
    </div>
  );
}

/* ──────────────────────────────────────────────
   BookShelfSkeleton — empty book state
────────────────────────────────────────────── */
function BookShelfSkeleton() {
  const variants = [
    { bg: 'oklch(var(--primary-700))', spine: 'oklch(var(--primary-800))', h: 220, w: 44, rot: 'rotate(-0.5deg)' },
    { bg: 'oklch(var(--secondary-800))', spine: 'oklch(var(--secondary-900))', h: 200, w: 52, rot: 'rotate(0.8deg)' },
    { bg: 'oklch(var(--background-300))', spine: 'oklch(var(--foreground-700)/0.5)', h: 212, w: 44, rot: 'rotate(-0.3deg)' },
    { bg: 'oklch(var(--accent-700))', spine: 'oklch(var(--accent-800))', h: 228, w: 52, rot: 'rotate(0.6deg)' },
    { bg: 'oklch(var(--foreground-800))', spine: 'oklch(var(--foreground-900))', h: 196, w: 44, rot: 'rotate(-0.7deg)' },
    { bg: 'oklch(var(--primary-200))', spine: 'oklch(var(--primary-400))', h: 208, w: 52, rot: 'rotate(0.4deg)' },
  ];
  return (
    <div className="relative">
      <div className="w-full h-px bg-foreground-950/8 mb-4" />
      <div className="flex items-end gap-[5px] md:gap-[7px] h-[260px] md:h-[280px] px-2">
        {variants.map((v, i) => (
          <div
            key={i}
            className="relative flex-shrink-0 overflow-hidden rounded-t-[2px] cursor-pointer book-spine-hover"
            style={{ width: v.w, height: v.h, backgroundColor: v.bg, transform: v.rot, boxShadow: '1px 0 4px rgba(0,0,0,0.08)' }}
          >
            <div className="absolute left-0 top-0 bottom-0 w-[6px]" style={{ backgroundColor: v.spine }} />
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.06] to-transparent" />
          </div>
        ))}
      </div>
      <div className="w-full h-[2px] bg-foreground-950/10" />
      <div className="w-full h-3" style={{ background: 'linear-gradient(to bottom,oklch(var(--foreground-950)/0.04),transparent)' }} />
    </div>
  );
}

/* ──────────────────────────────────────────────
   Home Page
────────────────────────────────────────────── */
export default function Home() {
  const books2026 = [...latestBooks2026, ...books.filter(b => b.dataStatus === '已確認')];

  return (
    <>
      <PageMeta
        title={siteSettings.seoTitle}
        description={siteSettings.description}
        canonical="https://fuko.sonbook.net/"
        ogTitle="複刻文化 Fuko｜歷史、人文社科、公版經典與小說"
        ogDescription="複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，透過整理、編輯與出版，讓不同時代的作品重新進入當代閱讀。"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': 'https://fuko.sonbook.net/#webpage',
          url: 'https://fuko.sonbook.net/',
          name: '複刻文化 Fuko｜歷史、人文社科、公版經典與小說',
          isPartOf: { '@id': 'https://fuko.sonbook.net/#website' },
          about: { '@id': 'https://fuko.sonbook.net/#organization' },
        }}
      />
      <Navbar />

      <main>
        {/* ═══════════════════════════════════════
            §1 · HERO — full screen editorial spread
        ═══════════════════════════════════════ */}
        <GreenValeHero />

        <OrnamentDivider />

        {/* ═══════════════════════════════════════
            §1 · 複刻文化的品牌定位
        ═══════════════════════════════════════ */}
        <RevealWrap>
          <section className="relative w-full overflow-hidden py-20 md:py-28" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
            {/* Ghost watermark */}
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute bottom-8 right-[6%] font-display italic font-bold select-none hidden lg:block" style={{ fontSize: 140, color: 'oklch(var(--foreground-950)/0.012)' }}>I</span>
            </div>

            <div className="w-full px-8 md:px-14 lg:px-20">
              <div className="mb-14 md:mb-16">
                <SectionLabel num="I" label="Positioning" />
              </div>

              <div className="max-w-[min(100%,1100px)] mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
                  <div className="lg:col-span-5 flex items-center">
                    <h2
                      className="font-heading font-bold text-foreground-900 leading-[1.15]"
                      style={{ fontSize: 'clamp(1.8rem,3.2vw,3rem)', letterSpacing: '-0.01em' }}
                    >
                      複刻文化的品牌定位
                    </h2>
                  </div>
                  <div className="lg:col-span-7 lg:pl-10 lg:border-l" style={{ borderColor: 'oklch(var(--foreground-950)/0.05)' }}>
                    <p className="text-foreground-700 leading-[1.85] text-[15px] mb-6">
                      複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，透過整理文本、補充閱讀脈絡與當代觀點，建立連結過去與現在的閱讀入口。
                    </p>
                    <p className="text-foreground-600 leading-[1.85] text-[15px]">
                      品牌所強調的「複刻」不是單純複製舊內容，而是重新理解作品背景、整理閱讀結構，並讓經典文本以更清楚的方式進入當代讀者的閱讀生活。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* ═══════════════════════════════════════
            §2 · 認識複刻文化 — 品牌資訊
        ═══════════════════════════════════════ */}
        <RevealWrap>
          <section className="relative w-full flex items-center justify-center overflow-hidden paper-texture" style={{ minHeight: '100dvh', backgroundColor: 'oklch(var(--background-100))' }}>
            {/* Ghost watermark */}
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute bottom-10 left-[6%] font-display italic font-bold select-none hidden lg:block" style={{ fontSize: 130, color: 'oklch(var(--foreground-950)/0.011)' }}>II</span>
            </div>

            <div className="relative w-full px-8 md:px-14 lg:px-20 py-24 md:py-28" style={{ zIndex: 1 }}>
              <div className="mb-14 md:mb-18">
                <SectionLabel num="II" label="Brand" />
              </div>

              <div className="max-w-[min(100%,1100px)] mx-auto">
                {/* ── Title ── */}
                <div className="mb-14">
                  <h2
                    className="font-heading font-bold text-foreground-900 leading-[1.15] mb-3"
                    style={{ fontSize: 'clamp(1.8rem,3.2vw,3rem)', letterSpacing: '-0.01em' }}
                  >
                    認識複刻文化
                  </h2>
                  <p className="text-foreground-500 leading-[1.8] text-[15px]">
                    歷史、人文、經典與小說閱讀
                  </p>
                </div>

                {/* ── Brand Identity + Introduction ── */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
                  <div className="lg:col-span-5">
                    <div className="mb-8">
                      <p className="font-label text-[11px] tracking-[0.15em] uppercase mb-4" style={{ color: 'oklch(var(--foreground-400)/0.50)' }}>正式名稱</p>
                      <p className="font-heading font-bold text-foreground-900 text-lg mb-1">複刻文化事業有限公司</p>
                      <p className="text-foreground-600 text-[14px] mt-3">網站簡稱：複刻文化</p>
                      <p className="text-foreground-600 text-[14px]">英文名稱：<span className="font-display italic font-semibold" style={{ color: 'oklch(var(--primary-500))' }}>FUKO</span></p>
                    </div>
                    <div>
                      <p className="font-label text-[11px] tracking-[0.15em] uppercase mb-3" style={{ color: 'oklch(var(--foreground-400)/0.50)' }}>出版定位</p>
                      <p className="font-heading font-bold text-foreground-900 text-base leading-relaxed">
                        歷史、人文社科、公版經典與小說
                      </p>
                    </div>
                  </div>

                  <div className="lg:col-span-7 lg:pl-10 lg:border-l" style={{ borderColor: 'oklch(var(--foreground-950)/0.05)' }}>
                    <p className="text-foreground-700 leading-[1.85] text-[15px] mb-5">
                      複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，透過重新整理與出版不同時代的作品及知識內容，讓具有閱讀價值的文字以新的形式與當代讀者相遇。網站呈現複刻文化的品牌定位、2026 最新發行書籍與相關出版資訊。
                    </p>
                    <p className="text-foreground-600 leading-[1.85] text-[14px]">
                      品牌名稱「複刻」代表重新整理、出版與呈現具有閱讀價值的內容，但不代表所有書籍皆為公版作品，也不代表對外提供文物修復、印刷複製或出版代工服務。
                    </p>
                  </div>
                </div>

                {/* ── Three Publishing Directions ── */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-10 mb-16">
                  <div className="vintage-corner p-6">
                    <h3 className="font-heading font-bold text-foreground-900 text-lg mb-3">歷史與人文社科</h3>
                    <p className="text-foreground-600 leading-[1.8] text-[14px]">
                      涵蓋歷史事件、人物、文化、思想及社會相關閱讀內容。
                    </p>
                  </div>
                  <div className="vintage-corner p-6">
                    <h3 className="font-heading font-bold text-foreground-900 text-lg mb-3">公版經典</h3>
                    <p className="text-foreground-600 leading-[1.8] text-[14px]">
                      整理並出版已確認可合法使用的經典作品，讓不同時代的內容重新進入當代閱讀。
                    </p>
                  </div>
                  <div className="vintage-corner p-6">
                    <h3 className="font-heading font-bold text-foreground-900 text-lg mb-3">小說</h3>
                    <p className="text-foreground-600 leading-[1.8] text-[14px]">
                      出版不同主題及類型的小說作品，呈現人物、時代與故事中的多元內容。
                    </p>
                  </div>
                </div>

                {/* ── Brand Relationship + Catalog CTA ── */}
                <div className="pt-10" style={{ borderTop: '1px solid oklch(var(--foreground-950)/0.06)' }}>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
                    <div className="lg:col-span-5">
                      <p className="font-label text-[11px] tracking-[0.15em] uppercase mb-4" style={{ color: 'oklch(var(--foreground-400)/0.50)' }}>品牌經銷</p>
                      <p className="text-foreground-700 leading-[1.85] text-[15px]">
                        本品牌委託崧博出版經銷，官方網站由崧博出版製作。
                      </p>
                    </div>
                    <div className="lg:col-span-7 lg:pl-10 flex flex-col">
                      <p className="text-foreground-500 leading-[1.85] text-[14px] mb-5">
                        完整書名、作者與出版資訊，請前往崧博書籍資料庫查詢。
                      </p>
                      <a
                        href={externalLinks.catalog}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 font-medium rounded-sm transition-colors duration-200 whitespace-nowrap text-sm self-start"
                        style={{ padding: '13px 28px', backgroundColor: 'oklch(var(--primary-500))', color: 'oklch(var(--background-50))' }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-600))')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-500))')}
                      >
                        前往書籍資料庫 <i className="ri-external-link-line text-xs" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* ═══════════════════════════════════════
            §3 · 主要出版方向
        ═══════════════════════════════════════ */}
        <RevealWrap>
          <section className="relative w-full overflow-hidden paper-texture py-20 md:py-28" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
            {/* Ghost watermark */}
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute top-[3%] right-[6%] font-display italic font-bold select-none hidden lg:block" style={{ fontSize: 130, color: 'oklch(var(--foreground-950)/0.011)' }}>III</span>
            </div>

            <div className="relative w-full px-8 md:px-14 lg:px-20 py-24 md:py-28" style={{ zIndex: 1 }}>
              <div className="mb-14 md:mb-18">
                <SectionLabel num="III" label="Directions" />
              </div>

              <div className="max-w-[min(100%,1100px)] mx-auto">
                {/* ── Title + Description ── */}
                <div className="mb-16">
                  <h2
                    className="font-heading font-bold text-foreground-900 leading-[1.15] mb-5"
                    style={{ fontSize: 'clamp(1.8rem,3.2vw,3rem)', letterSpacing: '-0.01em' }}
                  >
                    主要出版方向
                  </h2>
                  <p className="text-foreground-600 leading-[1.85] text-[15px] max-w-2xl">
                    複刻文化聚焦歷史、人文社科、公版經典與小說出版，透過整理與重新呈現不同時代的作品，延續經典內容的閱讀價值，也提供多元的人文與文學閱讀選擇。
                  </p>
                </div>

                {/* ── Four Direction Cards ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-10 mb-20">
                  {/* 歷史 */}
                  <div className="vintage-corner p-7">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full" style={{ backgroundColor: 'oklch(var(--primary-100))' }}>
                        <i className="ri-landscape-line text-[14px]" style={{ color: 'oklch(var(--primary-600))' }} />
                      </div>
                      <h3 className="font-heading font-bold text-foreground-900 text-xl">歷史</h3>
                    </div>
                    <p className="text-foreground-600 leading-[1.85] text-[14px]">
                      出版與歷史人物、事件、時代及文化發展相關的閱讀內容。
                    </p>
                  </div>

                  {/* 人文社科 */}
                  <div className="vintage-corner p-7">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full" style={{ backgroundColor: 'oklch(var(--secondary-100))' }}>
                        <i className="ri-lightbulb-line text-[14px]" style={{ color: 'oklch(var(--secondary-600))' }} />
                      </div>
                      <h3 className="font-heading font-bold text-foreground-900 text-xl">人文社科</h3>
                    </div>
                    <p className="text-foreground-600 leading-[1.85] text-[14px]">
                      出版與人文思想、社會議題及文化觀察相關的閱讀內容。
                    </p>
                  </div>

                  {/* 公版經典 */}
                  <div className="vintage-corner p-7">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full" style={{ backgroundColor: 'oklch(var(--accent-100))' }}>
                        <i className="ri-book-open-line text-[14px]" style={{ color: 'oklch(var(--accent-600))' }} />
                      </div>
                      <h3 className="font-heading font-bold text-foreground-900 text-xl">公版經典</h3>
                    </div>
                    <p className="text-foreground-600 leading-[1.85] text-[14px]">
                      讓不同時代的經典作品重新進入當代閱讀。
                    </p>
                  </div>

                  {/* 小說 */}
                  <div className="vintage-corner p-7">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full" style={{ backgroundColor: 'oklch(var(--primary-100))' }}>
                        <i className="ri-quill-pen-line text-[14px]" style={{ color: 'oklch(var(--primary-600))' }} />
                      </div>
                      <h3 className="font-heading font-bold text-foreground-900 text-xl">小說</h3>
                    </div>
                    <p className="text-foreground-600 leading-[1.85] text-[14px]">
                      出版不同題材與形式的小說作品。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* ═══════════════════════════════════════
            §4 · 2026 最新發行書籍 — Slider
        ═══════════════════════════════════════ */}
        <RevealWrap>
          <section className="relative w-full overflow-hidden paper-texture" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute top-[4%] right-[7%] font-display italic font-bold select-none hidden lg:block" style={{ fontSize: 130, color: 'oklch(var(--foreground-950)/0.011)' }}>IV</span>
            </div>

            <div className="relative w-full px-8 md:px-14 lg:px-20 py-24 md:py-28" style={{ zIndex: 1 }}>
              <div className="mb-10 md:mb-14">
                <SectionLabel num="IV" label="New Releases" />
              </div>

              <div className="max-w-[min(100%,1200px)] mx-auto">
                <h2
                  className="font-heading font-bold text-foreground-900 leading-[1.15] mb-3"
                  style={{ fontSize: 'clamp(1.8rem,3.2vw,3rem)', letterSpacing: '-0.01em' }}
                >
                  2026 最新發行書籍
                </h2>
                <p className="text-foreground-500 leading-[1.8] mb-10 max-w-lg" style={{ fontSize: 'clamp(0.85rem,1vw,0.95rem)' }}>
                  探索複刻文化於 2026 年最新發行的歷史、人文社科、經典與小說作品。
                </p>

                {(books2026.length === 0) ? (
                  <div className="py-20 text-center">
                    <i className="ri-book-2-line text-5xl block mb-5" style={{ color: 'oklch(var(--foreground-400)/0.18)' }} />
                    <p className="text-foreground-500 text-[15px] leading-relaxed">
                      目前尚無可顯示的2026最新發行書籍。
                    </p>
                  </div>
                ) : (
                  <>
                    <BookSlider books={books2026} />
                    {/* Section footer: catalog note */}
                    <div className="mt-14 pt-10" style={{ borderTop: '1px solid oklch(var(--foreground-950)/0.06)' }}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                        <p className="text-foreground-500 leading-[1.8] text-[14px] max-w-lg">
                          完整書名、作者、譯者及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
                        </p>
                        <a
                          href={externalLinks.catalog}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 font-medium rounded-sm transition-colors duration-200 whitespace-nowrap text-sm flex-shrink-0"
                          style={{ padding: '12px 24px', backgroundColor: 'oklch(var(--primary-500))', color: 'oklch(var(--background-50))' }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-600))')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-500))')}
                        >
                          前往書籍資料庫 <i className="ri-external-link-line text-xs" />
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* ═══════════════════════════════════════
            §5+6 · Catalog 入口 + 品牌經銷與合作（合併）
        ═══════════════════════════════════════ */}
        <RevealWrap>
          <section className="relative w-full overflow-hidden" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
            {/* Very subtle watermark */}
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span
                className="absolute top-1/2 left-1/2 font-display italic font-bold tracking-[0.12em] leading-none select-none"
                style={{
                  fontSize: 'clamp(90px,12vw,180px)',
                  color: 'oklch(var(--foreground-950)/0.015)',
                  transform: 'translate(-50%,-50%)',
                  whiteSpace: 'nowrap',
                }}
              >
                Catalogus
              </span>
            </div>

            <div className="relative w-full px-8 md:px-14 lg:px-20 py-20 md:py-24">
              <div className="max-w-[min(100%,1100px)] mx-auto">
                <div className="mb-14 md:mb-16">
                  <SectionLabel num="V" label="Catalog & Brand" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 mb-16">
                  <div className="lg:col-span-5">
                    <h2
                      className="font-heading font-bold leading-[1.15] mb-5"
                      style={{ fontSize: 'clamp(1.6rem,3vw,2.4rem)', color: 'oklch(var(--foreground-900))', letterSpacing: '-0.01em' }}
                    >
                      查詢完整書目
                    </h2>
                    <p
                      className="leading-[1.8] mb-6 max-w-md"
                      style={{ fontSize: 'clamp(0.85rem,1.05vw,0.95rem)', color: 'oklch(var(--foreground-600))' }}
                    >
                      如需確認複刻文化出版作品的完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博出版書籍資料庫查詢。
                    </p>
                    <a
                      href={externalLinks.catalog}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors btn-underline"
                      style={{ color: 'oklch(var(--foreground-500))' }}
                      onMouseEnter={e => (e.currentTarget.style.color = 'oklch(var(--foreground-900))')}
                      onMouseLeave={e => (e.currentTarget.style.color = 'oklch(var(--foreground-500))')}
                    >
                      查詢完整書目 <i className="ri-arrow-right-line" />
                    </a>
                  </div>

                  <div className="lg:col-span-7 lg:pl-10 lg:border-l" style={{ borderColor: 'oklch(var(--foreground-200)/0.30)' }}>
                    <h2
                      className="font-heading font-bold leading-[1.15] mb-5"
                      style={{ fontSize: 'clamp(1.6rem,3vw,2.4rem)', color: 'oklch(var(--foreground-900))', letterSpacing: '-0.01em' }}
                    >
                      品牌經銷與合作洽詢
                    </h2>
                    <p className="text-foreground-700 leading-[1.85] text-[15px] mb-3">
                      如有複刻文化品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過官方聯絡方式提出詢問。
                    </p>
                    <p className="text-foreground-500 leading-[1.85] text-[14px] mb-8">
                      品牌經銷與合作方案詳情請直接聯繫洽詢。
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={`mailto:${siteSettings.email}`}
                        className="inline-flex items-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                        style={{ padding: '11px 22px', backgroundColor: 'oklch(var(--secondary-700))', color: 'oklch(var(--background-50))' }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--secondary-800))')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--secondary-700))')}
                      >
                        聯絡品牌合作 <i className="ri-arrow-right-line text-xs" />
                      </a>
                      <a
                        href={externalLinks.songbo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                        style={{ padding: '11px 22px', border: '1px solid oklch(var(--foreground-300)/0.24)', color: 'oklch(var(--foreground-700))' }}
                        onMouseEnter={e => {
                          const el = e.currentTarget as HTMLElement;
                          el.style.backgroundColor = 'oklch(var(--background-100)/0.6)';
                          el.style.borderColor = 'oklch(var(--foreground-300)/0.40)';
                        }}
                        onMouseLeave={e => {
                          const el = e.currentTarget as HTMLElement;
                          el.style.backgroundColor = 'transparent';
                          el.style.borderColor = 'oklch(var(--foreground-300)/0.24)';
                        }}
                      >
                        前往崧博出版 <i className="ri-external-link-line text-xs" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* ═══════════════════════════════════════
            §7 · 收尾 CTA
        ═══════════════════════════════════════ */}
        <RevealWrap>
          <section className="relative w-full overflow-hidden" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute bottom-10 right-[6%] font-display italic font-bold select-none hidden lg:block" style={{ fontSize: 120, color: 'oklch(var(--foreground-950)/0.012)' }}>Fuko</span>
            </div>

            <div className="relative w-full px-8 md:px-14 lg:px-20 py-20 md:py-24">
              <div className="max-w-[min(100%,1000px)] mx-auto">
                <div className="mb-12 md:mb-14">
                  <SectionLabel num="VI" label="Contact" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
                  <div className="lg:col-span-5">
                    <h2
                      className="font-heading font-bold leading-[1.15] mb-5"
                      style={{ fontSize: 'clamp(1.6rem,3vw,2.6rem)', color: 'oklch(var(--foreground-900))', letterSpacing: '-0.01em' }}
                    >
                      從複刻文化，重新走進經典
                    </h2>
                    <p
                      className="leading-[1.8] mb-8 max-w-md"
                      style={{ fontSize: 'clamp(0.85rem,1.05vw,0.95rem)', color: 'oklch(var(--foreground-600))' }}
                    >
                      透過複刻文化目前公開的歷史、人文社科、公版經典與小說出版內容，查找符合個人興趣與閱讀需求的作品。
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link
                        to="/books"
                        className="inline-flex items-center justify-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                        style={{ padding: '13px 32px', backgroundColor: 'oklch(var(--primary-500))', color: 'oklch(var(--background-50))' }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-600))')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-500))')}
                      >
                        瀏覽經典書籍 <i className="ri-arrow-right-line" />
                      </Link>
                      <a
                        href={externalLinks.catalog}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                        style={{ padding: '13px 28px', border: '1px solid oklch(var(--foreground-300)/0.22)', color: 'oklch(var(--foreground-600))' }}
                        onMouseEnter={e => {
                          const el = e.currentTarget as HTMLElement;
                          el.style.borderColor = 'oklch(var(--foreground-400)/0.40)';
                          el.style.color = 'oklch(var(--foreground-800))';
                        }}
                        onMouseLeave={e => {
                          const el = e.currentTarget as HTMLElement;
                          el.style.borderColor = 'oklch(var(--foreground-300)/0.22)';
                          el.style.color = 'oklch(var(--foreground-600))';
                        }}
                      >
                        查詢完整書目 <i className="ri-external-link-line text-xs" />
                      </a>
                    </div>
                  </div>

                  <div className="lg:col-span-7 lg:pl-10 lg:border-l" style={{ borderColor: 'oklch(var(--foreground-200)/0.30)' }}>
                    <p className="text-foreground-600 leading-[1.85] text-[15px] mb-4">
                      複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，透過整理、編輯與出版，讓不同時代的作品以適合當代讀者的形式重新進入閱讀視野。
                    </p>
                    <p className="text-foreground-600 leading-[1.85] text-[15px] mb-4">
                      本品牌委託崧博出版經銷，官方網站由崧博出版製作。
                    </p>
                    <p className="text-foreground-500 leading-[1.85] text-[14px]">
                      完整書目請以崧博出版書籍資料庫為準；價格、版本、庫存、配送及購買方式則以各合作購書通路頁面為準。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        {/* ═══════════════════════════════════════
            合作購書通路
        ═══════════════════════════════════════ */}
        <RevealWrap>
          <section className="relative w-full overflow-hidden paper-texture" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute bottom-6 left-[6%] font-heading font-bold select-none hidden lg:block" style={{ fontSize: 90, color: 'oklch(var(--foreground-950)/0.011)' }}>通路</span>
            </div>

            <div className="relative w-full px-8 md:px-14 lg:px-20 py-16 md:py-20" style={{ zIndex: 1 }}>
              <div className="max-w-[min(100%,900px)] mx-auto text-center">
                <h2
                  className="font-heading font-bold text-foreground-900 leading-[1.15] mb-4"
                  style={{ fontSize: 'clamp(1.4rem,2.2vw,1.8rem)', letterSpacing: '-0.01em' }}
                >
                  複刻文化合作購書通路
                </h2>
                <p className="text-foreground-600 leading-[1.85] text-[15px] mb-2 max-w-xl mx-auto">
                  複刻文化相關書籍可透過合作購書通路查找。各作品在不同平台的上架狀態、版本、價格及庫存可能不同，實際資訊請以各通路頁面為準。
                </p>
                <p className="text-foreground-500 leading-[1.85] text-[14px] mb-7 max-w-lg mx-auto">
                  書籍價格、折扣、版本、庫存、配送與到貨資訊可能因平台而異，請以各合作購書通路頁面及實際結帳資訊為準。
                </p>
                <Link
                  to="/stores"
                  className="inline-flex items-center justify-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                  style={{ padding: '12px 28px', backgroundColor: 'oklch(var(--primary-500))', color: 'oklch(var(--background-50))' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-600))')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-500))')}
                >
                  前往通路查找 <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </section>
        </RevealWrap>

        {/* ═══════════════════════════════════════
            簡潔底部資訊
        ═══════════════════════════════════════ */}
        <section className="relative w-full overflow-hidden" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
          <div className="w-full px-8 md:px-14 lg:px-20 py-10 md:py-12">
            <div className="max-w-[min(100%,900px)] mx-auto">
              <div className="classical-rule mb-6" />
              <p className="text-foreground-500 leading-[1.8] text-[13px]">
                複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，透過整理、編輯與出版，讓不同時代的作品以適合當代讀者的形式重新進入閱讀視野。本品牌委託崧博出版經銷，官方網站由崧博出版製作。
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
